import store from '@/store'
import { useLazyQuery } from '@vue/apollo-composable';
import * as api from '@/api/okaso-api'
import { ApolloQueryResult } from '@apollo/client';


export type SearchItemCondition = {
  customerId?: number;
  largeCategoryCd?: number;
  middleCategoryCd?: number;
  smallCategoryCds?: number[];
  diameters?: string[];
  lengths?: string[];
  hasStockOnly?: boolean;
  keyword?: string;
}

export type SearchItemTake = number
export type SearchItemSkip = number

export function useSearchItemsFunc() {

  const getWhere = (condition: SearchItemCondition) => {
    const where: api.ItemWhereInput  = {
      AND: [],
    }

    // disabled=trueのものは表示しない --------------------
    where.AND?.push({
      disabled: {
        equals: false
      }
    })

    // 在庫があるもののみ --------------------
    let hasStockOnly = condition.hasStockOnly
    // 得意先で絞る --------------------
    let customerId = condition.customerId
    if (!customerId) {
      // ログイン中の得意先CDで絞り込む
      customerId = store.getters['loginUser/customer']?.id
    }
    where.AND?.push({
      stockItems: {
        some: {
          AND: [
            {
              stockItemUnitPrices: {
                some: {
                  customerId: {
                    equals: customerId
                  }
                }
              }
            },
            ...( !!hasStockOnly && [{ totalStockQuantity: { gt: 0 } }] || [])
          ],
        }
      },
    })

    // キーワードで絞る
    let keyword = condition.keyword
    if (!!keyword) {
      where.AND?.push({
        OR: [
          {
            name: { contains: keyword }
          },
          {
            description: { contains: keyword }
          },
          {
            largeCategory: {
              is: {
                name: { contains: keyword }
              }
            }
          },
          {
            middleCategory: {
              is: {
                name: { contains: keyword }
              }
            }
          },
          {
            smallCategory: {
              is: {
                name: { contains: keyword }
              }
            }
          }
        ]
      })
    }

    let largeCategoryCd = condition.largeCategoryCd
    if (!!largeCategoryCd) {
      where.AND?.push({
        largeCategory: {
          is: {
            largeCd: { equals: largeCategoryCd }
          }
        }
      })
    }
    
    let middleCategoryCd = condition.middleCategoryCd
    if (!!middleCategoryCd) {
      where.AND?.push({
        middleCategory: {
          is: {
            middleCd: { equals: middleCategoryCd }
          }
        }
      })
    }

    let smallCategoryCds = condition.smallCategoryCds
    if (!!smallCategoryCds && smallCategoryCds.length > 0) {
      where.AND?.push({
        smallCategory: {
          is: {
            smallCd: { in: smallCategoryCds }
          }
        }
      })
    }

    // 径で絞る --------------------
    let diameters = condition.diameters
    if (!!diameters && diameters.length > 0) {
      where.AND?.push({
        stockItems: {
          some: {
            diameter: { in: diameters }
          }
        }
      })
    }

    // 長さで絞る --------------------
    let lengths = condition.lengths
    if (!!lengths && lengths.length > 0) {
      where.AND?.push({
        stockItems: {
          some: {
            length: { in: lengths }
          }
        }
      })
    }

    return where
  }

  /// トータル件数取得
  const {
    load: searchItemsCountLoad,
    refetch: searchItemsCountRefetch,
    onResult: searchItemsCountOnResult,
    onError: searchItemsCountOnError,
    loading: searchItemsCountLoading,
  } = useLazyQuery<
    api.CountItemsQuery,
    api.CountItemsQueryVariables
  >(api.CountItemsDocument, undefined)

  const searchItemsCountFetchOrRefetch = async (variables:api.CountItemsQueryVariables) => {
    return await searchItemsCountLoad(api.CountItemsDocument, variables) || await searchItemsCountRefetch(variables)
  }

  const searchItemsCount = async ( condition: SearchItemCondition ) => {
    const where = getWhere(condition)
    // 検索実行
    const res = await searchItemsCountFetchOrRefetch({
      where,
    }) as ApolloQueryResult<api.CountItemsQuery>

    if (res.data?.aggregateItem) {
      return res.data.aggregateItem
    }
    return undefined
  }

  /// 商品取得
  const {
    load: searchItemsLoad,
    refetch: searchItemsRefetch,
    onResult: searchItemsOnResult,
    onError: searchItemsOnError,
    loading: searchItemsLoading,
  } = useLazyQuery<
    api.ItemsQuery,
    api.ItemsQueryVariables
  >(api.ItemsDocument, undefined)

  const searchItemsFetchOrRefetch = (variables:api.ItemsQueryVariables) => {
    searchItemsLoad(api.ItemsDocument, variables) || searchItemsRefetch(variables)
  }

  const searchItems_ = ( condition: SearchItemCondition ) => {
    const where = getWhere(condition)
    // 検索実行
    searchItemsFetchOrRefetch({
      where,
      orderBy: [
        {
          largeCd:{
            sort: api.SortOrder.Asc,
          }
        },
        {
          middleCd:{
            sort: api.SortOrder.Asc,
          }
        },
        {
          smallCd:{
            sort: api.SortOrder.Asc,
          }
        },
        {
          itemCd:{
            sort: api.SortOrder.Asc,
          }
        },
      ]
    })
  }

  const searchItems = ( condition: SearchItemCondition, take?: SearchItemTake, skip?: SearchItemSkip ) => {
    const where = getWhere(condition)
    // 検索実行
    searchItemsFetchOrRefetch({
      where,
      take,
      skip,
      orderBy: [
        {
          largeCategory:{
            seq: {
              sort: api.SortOrder.Asc,
            }
          },
        },
        {
          middleCategory: {
            seq: {
              sort: api.SortOrder.Asc,
            }
          },
        },
        {
          smallCategory: {
            seq: {
              sort: api.SortOrder.Asc,
            }
          },
        },
        {
          itemCd:{
            sort: api.SortOrder.Asc,
          }
        },
      ]
    })
  }

  return {
    searchItemsCount,
    searchItemsCountOnResult,
    searchItemsCountOnError,
    searchItems,
    searchItemsOnResult,
    searchItemsOnError,
    searchItemsLoading,
  };
}
